import type { ColDef } from "@ag-grid-community/core";
import { t } from "@lingui/core/macro";
/* eslint-disable class-methods-use-this */
import { clsx, EMPTY_ARRAY, noop } from "@regrello/core-utils";
import { DataTestIds } from "@regrello/data-test-ids-api";
import {
  type DocumentFields,
  type FieldFields,
  type FieldInstanceFields,
  FormConstraintConditionOperator,
  type FormFieldFields,
  PropertyDataType,
  type PropertyTypeFields,
  type SpectrumFieldValidationTypeFields,
  type SpectrumValueConstraintFields,
} from "@regrello/graphql-api";
import type { RegrelloIconName } from "@regrello/ui-core";
import type { ReactNode } from "react";
import type { FieldArrayWithId, FieldPath, FieldValues, UseFormReturn } from "react-hook-form";

import type { FieldInstanceBaseFields } from "../../../../../types";
import type { ConfigureSpectrumFieldFormFormFields } from "../../../../views/modals/formDialogs/spectrumFields/_internal/ConfigureSpectrumFieldForm";
import type {
  CustomFieldPlugin,
  CustomFieldPluginV2RenderFormFieldProps,
  ValidateBulkUploadCellValueOptions,
} from "../../../customFields/plugins/types/CustomFieldPlugin";
import type { ValidationResult } from "../../../customFields/plugins/utils/validateBulkUploadCellValueUtils";
import { RegrelloControlledFormFieldSignature } from "../../../formFields/controlled/regrelloControlledFormFieldSpectrumSignature";
import { RegrelloFormFieldSignature } from "../../../formFields/RegrelloFormFieldSignature";
import {
  type ProspectiveSpectrumFieldVersion,
  SpectrumFieldPluginDecorator,
} from "../../types/SpectrumFieldPluginDecorator";
import { SpectrumFieldValidationType } from "../../types/SpectrumFieldValidationType";

type SignatureFieldPluginFrontendValue = DocumentFields[];

export class SpectrumSignatureFieldPluginDecorator<
  T extends SignatureFieldPluginFrontendValue,
> extends SpectrumFieldPluginDecorator<T> {
  constructor(plugin: CustomFieldPlugin<T>) {
    super(plugin);
    this.uri = "com.regrello.spectrumField.signature";
  }

  public canProcessValidationType(spectrumValidationType: ProspectiveSpectrumFieldVersion["validationType"]) {
    return spectrumValidationType.validationType === SpectrumFieldValidationType.SIGNATURE;
  }

  public canProcessSpectrumField(field: ProspectiveSpectrumFieldVersion) {
    return (
      this.canProcessPropertyDataType(field.propertyType.dataType) &&
      this.canProcessValidationType(field.validationType)
    );
  }

  public override renderSpectrumFormField = <TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>>(
    _field: FieldFields,
    props: CustomFieldPluginV2RenderFormFieldProps<TFieldValues, TName>,
  ): React.ReactNode => {
    const isDeleted = props.fieldInstance?.field.deletedAt != null;
    return (
      <RegrelloControlledFormFieldSignature
        {...props}
        key={props.controllerProps.name}
        dataTestId={DataTestIds.CUSTOM_FIELD_VALUE_INPUT}
        infoTooltipText={props.fieldInstance?.field?.description ?? undefined}
        isDeleted={isDeleted}
        onUploadFinish={props.onChangeFinish ?? noop}
        onUploadStart={props.onChangeStart ?? noop}
      />
    );
  };

  public override getFieldDisplayName = () => {
    return t`Signature`;
  };

  public override getIconName = (): RegrelloIconName => {
    return "signature-field";
  };

  public findPropertyTypeFromLoadedPropertyTypes(propertyTypes: PropertyTypeFields[]) {
    return propertyTypes.find((propertyType) => propertyType.dataType === PropertyDataType.DOCUMENT_ID);
  }

  public findValidationTypeFromLoadedValidationTypes(validationTypes: SpectrumFieldValidationTypeFields[]) {
    return validationTypes.find(
      (validationType) => validationType.validationType === SpectrumFieldValidationType.SIGNATURE,
    );
  }

  public findValueConstraintsFromLoadedValueConstraints(_valueConstraints: SpectrumValueConstraintFields[]) {
    return EMPTY_ARRAY;
  }

  public getConstraintConditionOperators() {
    return {
      [FormConstraintConditionOperator.IS_ZERO]: {
        label: t`Is empty`,
        inputCount: 0,
        isMultiselect: false,
      },
      [FormConstraintConditionOperator.IS_NOT_ZERO]: {
        label: t`Is not empty`,
        inputCount: 0,
        isMultiselect: false,
      },
    };
  }

  public getSpectrumFormAutosaveMode() {
    return "onChange" as const;
  }

  public renderPreviewFormField() {
    return (
      <RegrelloFormFieldSignature disabled={true} onChange={noop} showLegalNotice={true} size="medium" value={[]} />
    );
  }

  public override renderDisplayValue(
    fieldInstance: FieldInstanceFields | FieldInstanceBaseFields,
    options: {
      context: "dataTab";
    },
  ): ReactNode {
    const values = this.getValueForFrontend(fieldInstance);
    return (
      <RegrelloFormFieldSignature
        className={clsx({ "w-full !mb-0": options.context === "dataTab" })}
        disabled={true}
        onChange={noop}
        showPreviewOnly={true}
        value={values}
      />
    );
  }

  public renderValueConstraints(_props: {
    constraints: Array<FieldArrayWithId<ConfigureSpectrumFieldFormFormFields, "valueConstraints">>;
    disabled: boolean;
    form: UseFormReturn<ConfigureSpectrumFieldFormFormFields>;
  }): ReactNode {
    return null;
  }

  public override validateBulkUploadCellValue(
    _field: FieldFields,
    _value: string,
    _options: ValidateBulkUploadCellValueOptions,
  ): ValidationResult {
    throw new Error("Not implemented");
  }

  public getColumnDef<RowDatum, T>(
    _fieldInstance: FieldInstanceFields,
    _formField: FormFieldFields,
    colDefParams: Partial<ColDef<RowDatum, T>>,
  ): ColDef<RowDatum, T> {
    return colDefParams;
  }
}
